import { Format } from '../Format'

const COVER_SHEET_ID = 'cover'

/*
  Adapter: подсовывает обложку в интерфейс Sheet, чтобы DesignerPreview / DesignerWorkspace /
  DesignerSheet работали с ней как с обычным разворотом.

  Отличия от Sheet: между страницами рендерится корешок, а ширина листа считается с его
  учётом (totalWidthMm). Лист не хранится в Firestore — он собирается на лету из страниц
  обложки и данных проекта.
*/
class CoverSheet {
  _id = COVER_SHEET_ID
  pages
  format
  spine
  barcodes
  backSide
  fontsLink
  order = -1

  constructor (data) {
    this.pages = (data.pages || []).filter(Boolean)
    this.format = data.format instanceof Format ? data.format : new Format(data.format)
    this.spine = data.spine
    // Блок штрихкодов на задней обложке (CoverBarcodes) — та же логика, что в печати.
    this.barcodes = data.barcodes || null
    // Сторона задней страницы (表4): при корешке слева она левая, при корешке
    // справа (вертикальная вёрстка) — правая; штрихкоды позиционируются по ней.
    this.backSide = data.backSide || 'left'
    // <link> на японские Google Fonts: подключается на уровне листа, чтобы список
    // шрифтов не был вшит в сохранённые страницы обложки.
    this.fontsLink = data.fontsLink || ''
  }

  get isCover () {
    return true
  }

  get leftPage () {
    return this.pages.find((page) => page.side === 'left')
  }

  get rightPage () {
    return this.pages.find((page) => page.side === 'right')
  }

  get backPage () {
    return this.pages.find((page) => page.side === this.backSide)
  }

  get spineWidthMm () {
    return this.spine?.widthMm || 0
  }

  /* Полная ширина листа: две стороны обложки плюс корешок. */
  get totalWidthMm () {
    return this.format.width * this.pages.length + this.spineWidthMm
  }

  get aspectRatio () {
    return this.totalWidthMm / this.format.height
  }

  render (contenteditable = false, rootEl = document) {
    return this.#renderLayout(contenteditable, rootEl) + this.#renderStyle()
  }

  clone () {
    return new CoverSheet({
      pages: this.pages.map((page) => page.clone()),
      format: this.format,
      spine: this.spine,
      barcodes: this.barcodes,
      backSide: this.backSide,
      fontsLink: this.fontsLink
    })
  }

  /* Штрихкоды рисуются на задней обложке (как в печати). */
  get #showBarcodes () {
    return Boolean(this.barcodes && this.backPage)
  }

  /* Смещение плашки штрихкодов = положение задней страницы внутри листа. */
  get #barcodesOffsetMm () {
    return this.backSide === 'left' ? 0 : this.format.width + this.spineWidthMm
  }

  #renderLayout (contenteditable, rootEl) {
    const renderedLeftPage = this.leftPage?.render(contenteditable, rootEl) || ''
    const renderedRightPage = this.rightPage?.render(contenteditable, rootEl) || ''
    const renderedSpine = this.spine?.renderLayout() || ''
    const renderedBarcodes = this.#showBarcodes ? this.barcodes.renderLayout() : ''

    return `${this.fontsLink}<div class="sheet cover-sheet" id="${this._id}">${renderedLeftPage}${renderedSpine}${renderedRightPage}${renderedBarcodes}</div>`
  }

  #renderStyle () {
    return `
      <style>
        #${this._id}.sheet {
          width: ${this.totalWidthMm}${this.format.units};
          height: ${this.format.getCssHeight()};
          background: white;
          display: flex;
          position: relative;
        }
        ${this.spine?.renderStyle(this._id) || ''}
        ${this.#showBarcodes ? this.barcodes.renderStyle(this._id, this.format.width, this.#barcodesOffsetMm) : ''}
      </style>
    `
  }
}

export { CoverSheet, COVER_SHEET_ID }
